import { Badge, InlineStack, Text, Tooltip } from '@shopify/polaris'
import type { Lens, PeriodMaturity } from '../../sdk/fc.api'

// Every card in the Control Center states which clock it is reading, because a
// performance figure and a cash figure that both say "this month" describe
// different sets of orders. See docs/cleanup-architecture.md §9.2.

const LENS_COPY: Record<Lens, { label: string; help: string }> = {
  performance: {
    label: 'Performance',
    help:  'Grouped by order date — measures how well the team worked this period.',
  },
  cash: {
    label: 'Cash',
    help:  'Grouped by settlement date — measures money that actually arrived, whenever the order was placed.',
  },
}

export function LensBadge({ lens }: { lens: Lens }) {
  const { label, help } = LENS_COPY[lens]
  return (
    <Tooltip content={help}>
      <Badge tone={lens === 'cash' ? 'success' : 'info'}>{label}</Badge>
    </Tooltip>
  )
}

// Performance figures keep moving until the period's orders reach a terminal
// state. Showing the percentage stops a mid-month number being read as a result.
export function MaturityNote({ maturity }: { maturity: PeriodMaturity }) {
  const pct = Math.round(maturity.maturityPct)

  return (
    <Tooltip
      content={
        maturity.isFinal
          ? `${maturity.resolvedOrders} of ${maturity.totalOrders} orders have reached a final state.`
          : `Only ${maturity.resolvedOrders} of ${maturity.totalOrders} orders have reached a final state — ${maturity.pendingOrders} are still in progress, so these figures will change.`
      }
    >
      <InlineStack gap="100" blockAlign="center">
        {!maturity.isFinal && <Badge tone="attention">Provisional</Badge>}
        <Text as="span" variant="bodySm" tone="subdued">
          {`${pct}% settled`}
        </Text>
      </InlineStack>
    </Tooltip>
  )
}

// Header row shared by every BCC card: title on the left, lens (and maturity for
// performance cards) on the right.
export function CardHeader({
  title,
  lens,
  maturity,
}: {
  title:     string
  lens:      Lens
  maturity?: PeriodMaturity
}) {
  return (
    <InlineStack align="space-between" blockAlign="center">
      <Text as="h2" variant="headingMd">{title}</Text>
      <InlineStack gap="200" blockAlign="center">
        {maturity && <MaturityNote maturity={maturity} />}
        <LensBadge lens={lens} />
      </InlineStack>
    </InlineStack>
  )
}
